/** A digit run this long or longer matches anywhere inside a number. */
export const MIN_DIGIT_FRAGMENT = 4;
/**
 * Words beyond this many are ignored. Each word adds an EXISTS subquery
 * over emails (and one over phone numbers for a digit word), and Cosmos
 * caps the joins in one query, so this keeps a query within it.
 */
export const MAX_SEARCH_WORDS = 5;
export function classifySearchQuery(q) {
    if (q.includes('@') && !/\s/.test(q.trim()))
        return 'email';
    const stripped = q.replace(/[\s\-().]/g, '');
    if (stripped.length >= 2 && /^\+?\d+$/.test(stripped))
        return 'phone';
    return 'text';
}
/** The words a text query matches on: lower-cased, at most MAX_SEARCH_WORDS. */
export function searchWords(q) {
    return q.trim().toLowerCase().split(/\s+/).filter(Boolean).slice(0, MAX_SEARCH_WORDS);
}
/**
 * The domain part of an email-looking query, when it looks like a real
 * domain ("acme.com", "acme.co.uk"); null otherwise ("bob@", "bob@acme").
 */
export function emailDomain(q) {
    const at = q.lastIndexOf('@');
    if (at < 0)
        return null;
    const domain = q.slice(at + 1).trim().toLowerCase().replace(/\.$/, '');
    if (domain.length < 3 || !domain.includes('.') || domain.startsWith('.'))
        return null;
    return domain;
}
/**
 * The host name of a website as typed, lower-cased and without a leading
 * "www.": "https://www.Acme.com/about" → "acme.com", "acme.co.uk:8080" →
 * "acme.co.uk". Undefined when there's no plausible host (no dot). Stored
 * on contacts as `websiteHost` so an email search can match a company's
 * domain exactly.
 */
export function websiteHost(website) {
    if (!website)
        return undefined;
    let s = website.trim().toLowerCase();
    s = s.replace(/^[a-z][a-z0-9+.-]*:\/\//, '').replace(/^\/\//, '');
    s = s.split(/[/?#]/, 1)[0] ?? '';
    s = s.slice(s.lastIndexOf('@') + 1); // drop any user:pass@
    s = s.replace(/:\d*$/, '').replace(/\.$/, '').replace(/^www\./, '');
    if (!s.includes('.') || s.startsWith('.') || /\s/.test(s))
        return undefined;
    return s;
}
/** True when a company's website is on `domain` or one of its subdomains. */
export function hostOnDomain(host, domain) {
    return !!host && (host === domain || host.endsWith(`.${domain}`));
}
function digitsOf(s) {
    return s.replace(/\D+/g, '');
}
/** Digit-fragment rule: in the number as typed, in its national form or
 *  in E.164, or (for a national number with its leading 0) E.164 without
 *  the 0. */
function numberContainsDigits(p, digits) {
    const typed = p.digits ?? digitsOf(p.number ?? '');
    const e164 = p.e164 ?? '';
    if (typed.includes(digits) || e164.includes(digits) || (p.national ?? '').includes(digits))
        return true;
    const national = digits.replace(/^0+/, '');
    return national !== digits && national.length >= MIN_DIGIT_FRAGMENT && e164.includes(national);
}
/**
 * The search rules in memory: true when `contact` matches query `q`. The API
 * runs the same rules as Cosmos SQL (see the module comment).
 *
 *  - text:  every word must match (any order), each in any of display name,
 *           company, job title, department, the address's city, website,
 *           notes or an email address; a word of 4+ digits also matches
 *           inside a phone number.
 *  - phone: a number starting with the fragment, or, for 4+ digits,
 *           containing it anywhere.
 *  - email: an address starting with the query (containing it, for
 *           "@domain"), or a company whose website is on the query's domain.
 */
export function contactMatchesSearch(contact, rawQ) {
    const q = rawQ.trim().toLowerCase();
    if (!q)
        return true;
    const phones = contact.phoneNumbers ?? [];
    const emails = (contact.emails ?? []).map((e) => (e.address ?? '').toLowerCase());
    const kind = classifySearchQuery(q);
    if (kind === 'phone') {
        const phoneQ = q.replace(/[\s\-().]/g, '');
        const digits = digitsOf(phoneQ);
        return phones.some((p) => (p.e164 ?? '').startsWith(phoneQ) ||
            (p.number ?? '').startsWith(phoneQ) ||
            (digits.length >= MIN_DIGIT_FRAGMENT && numberContainsDigits(p, digits)));
    }
    if (kind === 'email') {
        const addressHit = emails.some((a) => (q.startsWith('@') ? a.includes(q) : a.startsWith(q)));
        if (addressHit)
            return true;
        const domain = emailDomain(q);
        return (!!domain &&
            contact.kind === 'company' &&
            hostOnDomain(contact.websiteHost ?? websiteHost(contact.website), domain));
    }
    const fields = [
        contact.displayName,
        contact.company,
        contact.jobTitle,
        contact.department,
        contact.address?.city,
        contact.website,
        contact.notes
    ]
        .filter((f) => typeof f === 'string')
        .map((f) => f.toLowerCase());
    return searchWords(q).every((w) => fields.some((f) => f.includes(w)) ||
        emails.some((a) => a.includes(w)) ||
        (/^\d+$/.test(w) && w.length >= MIN_DIGIT_FRAGMENT && phones.some((p) => numberContainsDigits(p, w))));
}
//# sourceMappingURL=search.js.map